/* eslint-disable no-shadow */
import { END, buffers, eventChannel } from "redux-saga";
import {
    call,
    cancel,
    cancelled,
    delay,
    fork,
    race,
    take,
    takeEvery
} from "redux-saga/effects";
import noop from "lodash.noop";
import { ONE_SECOND } from "Globals/constants/constants";
import { takeFirstAndLatest, withBatchedPromises, withRefreshRequest } from "Utils/sagaUtils";

import {
    LOAD_REQUESTED,
    SUBSCRIBE,
    UNSUBSCRIBE,
    UPDATE_REQUESTED,
    defaultScopeId,
    failNotificationSettingsLoad,
    failNotificationSettingsUpdate,
    requestNotificationSettingsLoad,
    succeedNotificationSettingsLoad,
    succeedNotificationSettingsUpdate,
} from "./notificationSettings.duck";

import {
    loadNotificationSettings,
    subscribeNotificationSettings,
    updateNotificationSettings,
} from "Globals/dataTypes/notificationSettings/notificationSettings.api";

import { getId, injectApiWithContextFactory, injectBoundActionCreatorsFactory } from "Utils/sagaUtils";
import { injectBoundGQLClient } from "Utils/injectBoundGQLClient";

export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        failNotificationSettingsLoad,
        failNotificationSettingsUpdate,
        requestNotificationSettingsLoad,
        succeedNotificationSettingsLoad,
        succeedNotificationSettingsUpdate,
    },
    getId,
    defaultScopeId
);

export const withApiContext = injectApiWithContextFactory(
    {
        loadNotificationSettings,
        subscribeNotificationSettings,
        updateNotificationSettings,
    },
    injectBoundGQLClient
);

export function* updateNotificationSettingSaga({
    data,
    api: { updateNotificationSettings = noop },
    actions: {
        succeedNotificationSettingsUpdate,
        failNotificationSettingsUpdate,
    },
    resolve = noop,
    reject = noop,
}) {
    try {
        const {
            data: { updateProjectNotificationSettings: updatedSetting },
        } = yield updateNotificationSettings({ notificationSettings: data });
        yield succeedNotificationSettingsUpdate(updatedSetting);
        yield call(resolve);
    } catch (e) {
        yield failNotificationSettingsUpdate(e);
        yield call(reject, e);
    }
}

export function* loadNotificationSettingsSaga({
    api: { loadNotificationSettings },
    actions: { succeedNotificationSettingsLoad, failNotificationSettingsLoad },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield loadNotificationSettings();
        yield succeedNotificationSettingsLoad(result);
        yield call(resolve);
    } catch (e) {
        yield failNotificationSettingsLoad(e);
        yield call(reject, e);
    }
}

export const notificationSettingsSubscription = ({ subject, mdContext }) => {
    return eventChannel(
        emitter => {
            try {
                // We have to manuall inject the client here as this isn't a saga
                return injectBoundGQLClient(subscribeNotificationSettings, mdContext)({ subject, emitter });
            } catch (e) {
                emitter(END);
            }
        },
        buffers.expanding(1) // buffer one invocation holding the most recent event
    );
};

export function* subscribeToNotificationSettings({
    subject,
    actions: { succeedNotificationSettingsLoad },
    mdContext
}) {
    const chan = yield call(notificationSettingsSubscription, ({ subject, mdContext }));
    try {
        while (true) {
            const notificationSettings = yield take(chan);
            if (notificationSettings) {
                const {
                    data: { subscribeNotificationSetting: updatedSetting },
                } = notificationSettings;
                yield succeedNotificationSettingsLoad(updatedSetting);
            }
            yield delay(ONE_SECOND);
        }
    } finally {
        if (yield cancelled()) {
            chan.close();
        }
    }
}

export function* subscribeNotificationSettingsSaga() {
    const subscriptions = {};
    const tasks = {};
    while (true) {
        const { subscribe, unsubscribe } = yield race({
            subscribe: take(SUBSCRIBE),
            unsubscribe: take(UNSUBSCRIBE),
        });
        const {
            data: { userId },
        } = subscribe || unsubscribe;
        const ids = [userId].filter(i => i);
        if (subscribe) {
            for (const subId of ids) {
                subscriptions[subId] = subscriptions[subId] || 0;
                subscriptions[subId]++;
                if (subscriptions[subId] === 1) {
                    tasks[subId] = yield fork(
                        withApiContext(withBoundActions(subscribeToNotificationSettings)),
                        { ...(subscribe || unsubscribe), subject: subId }
                    );
                }
            }
        }
        if (unsubscribe) {
            for (const subId of ids) {
                subscriptions[subId]--;
                if (subscriptions[subId] === 0) {
                    yield cancel(tasks[subId]);
                }
            }
        }
    }
}

export default () =>
    function* rootSaga() {
        yield takeFirstAndLatest(
            UPDATE_REQUESTED,
            withBatchedPromises(withApiContext(withBoundActions(updateNotificationSettingSaga))),
            ONE_SECOND / 3,
        );
        yield takeEvery(
            LOAD_REQUESTED,
            withRefreshRequest(withApiContext(withBoundActions(loadNotificationSettingsSaga)))
        );
        yield fork(subscribeNotificationSettingsSaga);
    };
